import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useAppSelector, useAppDispatch } from "../../../redux/store";
import {
  Box,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useSnackbar } from "notistack";

import {
  fetchVendorInsuranceStatus,
  clearInsuranceStatus,
} from "../../../redux/slices/vendorsSlice";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import Table from "../../../components/Table";
import VendorDetailDrawer from "./components/VendorDetailDrawer";
import { KpiCard, PageHeader } from "../../../ui";

const VendorAudit = () => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const {
    insuranceStatus,
    insuranceStatusSummary,
    insuranceStatusStatus,
    insuranceStatusError,
    insuranceStatusTotal,
  } = useAppSelector((state) => state.vendors) as any;

  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, 400);
  const [certFilter, setCertFilter] = useState<string>("all");
  const [folderFilter, setFolderFilter] = useState<string>("all");

  // Pagination + sorting state (0-based page, matches the table)
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [sortBy, setSortBy] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Bumped after vendor mutations (e.g. email edit) so the list refetches.
  const [refreshNonce, setRefreshNonce] = useState<number>(0);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);

  const handleViewVendor = useCallback((vendorId: string | number) => {
    setSelectedVendorId(String(vendorId));
    setDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedVendorId(null);
  }, []);

  // Whenever the search/filters/year change we refetch from page 0. The
  // filterKey tracks only the query inputs (not page/sort), so changing a
  // filter resets the page exactly once and triggers a single refetch.
  const filterKey = `${year}|${debouncedSearch}|${certFilter}|${folderFilter}`;
  const [queryKey, setQueryKey] = useState(filterKey);
  const prevFilterKey = useRef(filterKey);

  useEffect(() => {
    if (prevFilterKey.current !== filterKey) {
      prevFilterKey.current = filterKey;
      setPage(0);
      setQueryKey(filterKey);
    }
  }, [filterKey]);

  useEffect(() => {
    dispatch(
      fetchVendorInsuranceStatus({
        year,
        page,
        pageSize: rowsPerPage,
        search: debouncedSearch || undefined,
        certFilter,
        folderFilter,
        sortBy,
        sortOrder,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, queryKey, refreshNonce, page, rowsPerPage, sortBy, sortOrder]);

  // Clear status when the page unmounts (not on every refetch).
  useEffect(() => {
    return () => {
      dispatch(clearInsuranceStatus());
    };
  }, [dispatch]);

  useEffect(() => {
    if (insuranceStatusStatus === "failed" && insuranceStatusError) {
      enqueueSnackbar(`Failed to load vendor audit data: ${insuranceStatusError}`, {
        variant: "error",
      });
    }
  }, [insuranceStatusStatus, insuranceStatusError, enqueueSnackbar]);

  const handleYearChange = (e: any) => {
    setYear(Number(e.target.value));
  };

  const handleCertFilterChange = (e: any) => {
    setCertFilter(e.target.value);
  };

  const handleFolderFilterChange = (e: any) => {
    setFolderFilter(e.target.value);
  };

  const handleSortChange = useCallback((by: string, ord: "asc" | "desc") => {
    setSortBy(by);
    setSortOrder(ord);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  }, []);

  // Summary card data (server returns global counts over the full report)
  const summary = insuranceStatusSummary || {};

  const summaryCards = [
    {
      label: "Total Vendors",
      value: summary.totalVendors ?? 0,
      color: "#555759",
    },
    {
      label: "With Insurance",
      value: summary.withAnyCert ?? 0,
      color: "#059669",
    },
    {
      label: "Missing Insurance",
      value: summary.missingCert ?? 0,
      color: summary.missingCert > 0 ? "#dc2626" : "#059669",
    },
    {
      label: "WC Certificates",
      value: summary.withWCCert ?? 0,
      color: "#f59e0b",
    },
    {
      label: "GL Certificates",
      value: summary.withGLCert ?? 0,
      color: "#555759",
    },
    {
      label: "Dropbox Folders",
      value: summary.withFolder ?? 0,
      color: "#555759",
    },
  ];

  const tableHeaders = useMemo(
    () => [
      {
        key: "name",
        displayName: "Vendor Name",
        minWidth: "180px",
      },
      {
        key: "email",
        displayName: "Email",
        minWidth: "200px",
        format: ({ value }: any) => value || "-",
      },
      {
        key: "accountNumber",
        displayName: "Account #",
        format: ({ value }: any) => value || "-",
      },
      {
        key: "hasDropboxFolder",
        displayName: "Dropbox Folder",
        format: ({ value, row }: any) =>
          value ? (
            <Chip
              label={row.dropboxFolderName || "Yes"}
              color="success"
              size="small"
              variant="outlined"
            />
          ) : (
            <Chip label="No" color="default" size="small" variant="outlined" />
          ),
      },
      {
        key: "hasWCCert",
        displayName: "WC Certificate",
        format: ({ value, row }: any) =>
          value ? (
            <Chip
              label={row.wcFileName || "Found"}
              color="warning"
              size="small"
              variant="outlined"
            />
          ) : (
            <Chip label="N/A" color="default" size="small" variant="outlined" />
          ),
      },
      {
        key: "hasGLCert",
        displayName: "GL Certificate",
        format: ({ value, row }: any) =>
          value ? (
            <Chip
              label={row.glFileName || "Found"}
              color="secondary"
              size="small"
              variant="outlined"
            />
          ) : (
            <Chip label="N/A" color="default" size="small" variant="outlined" />
          ),
      },
      {
        key: "hasPayments",
        displayName: "Has Payments",
        format: ({ value }: any) =>
          value ? (
            <Chip label="Yes" color="info" size="small" variant="outlined" />
          ) : (
            <Chip label="No" color="default" size="small" variant="outlined" />
          ),
      },
      {
        key: "latestPayment",
        displayName: "Last Payment",
        format: ({ value }: any) => value || "-",
      },
      {
        key: "archived",
        displayName: "Status",
        format: ({ value }: any) =>
          value ? (
            <Chip label="Archived" color="error" size="small" variant="outlined" />
          ) : (
            <Chip label="Active" color="success" size="small" variant="outlined" />
          ),
      },
    ],
    []
  );

  return (
    <Box>
      <PageHeader
        eyebrow="Vendors"
        title="Vendor Audit"
        summary="Every vendor in the Intuit book, their insurance certificates, and their Dropbox construction folder."
      />

      {/* Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {summaryCards.map((card) => (
          <Grid key={card.label} size={{ xs: 6, sm: 4, md: 2 }}>
            <KpiCard
              label={card.label}
              value={
                insuranceStatusStatus === "loading" ? (
                  <CircularProgress size={20} />
                ) : (
                  card.value
                )
              }
              status={
                card.label === "Missing Insurance" && card.value > 0
                  ? "red"
                  : undefined
              }
            />
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <TextField
          size="small"
          placeholder="Search vendors..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Insurance</InputLabel>
          <Select
            value={certFilter}
            label="Insurance"
            onChange={handleCertFilterChange}
          >
            <MenuItem value="all">All Vendors</MenuItem>
            <MenuItem value="hasAny">Has Any Certificate</MenuItem>
            <MenuItem value="hasWC">Has WC Certificate</MenuItem>
            <MenuItem value="hasGL">Has GL Certificate</MenuItem>
            <MenuItem value="missing">Missing Certificates</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Dropbox Folder</InputLabel>
          <Select
            value={folderFilter}
            label="Dropbox Folder"
            onChange={handleFolderFilterChange}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="hasFolder">Has Folder</MenuItem>
            <MenuItem value="noFolder">No Folder</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Year</InputLabel>
          <Select
            value={year}
            label="Year"
            onChange={handleYearChange}
          >
            {[2024, 2025, 2026, 2027].map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Error state */}
      {insuranceStatusStatus === "failed" && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {insuranceStatusError}
        </Alert>
      )}

      {/* Table — server paginated/filtered/sorted */}
      <Table
        headers={tableHeaders}
        rows={insuranceStatus}
        loading={insuranceStatusStatus === "loading"}
        serverPagination
        totalCount={insuranceStatusTotal}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
        actions={{
          view: handleViewVendor,
        }}
      />

      {/* Vendor Detail Drawer */}
      <VendorDetailDrawer
        open={drawerOpen}
        vendorId={selectedVendorId}
        year={year}
        onClose={handleCloseDrawer}
        onVendorUpdated={() => setRefreshNonce((n) => n + 1)}
      />
    </Box>
  );
};

export default VendorAudit;
